import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  initJiraAuth, 
  checkJiraStatus, 
  disconnectJira, 
  getJiraSites, 
  switchJiraSite,
  getJiraProjects
} from '../../../api/integrations';

const JiraIntegrationForm = ({ onBack }) => {
  const [status, setStatus] = useState({
    isConnected: false,
    jiraInstanceUrl: '',
    loading: true,
    error: null
  });
  const [sites, setSites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Check if user is already connected to Jira
  useEffect(() => {
    checkJiraConnection();
  }, []);

  // Fetch sites when connection is established
  useEffect(() => {
    if (status.isConnected) {
      fetchJiraSites();
      fetchJiraProjects();
    }
  }, [status.isConnected]);

  const checkJiraConnection = async () => {
    try {
      setLoading(true);
      const response = await checkJiraStatus();
      setStatus({
        isConnected: response.is_connected,
        jiraInstanceUrl: response.jira_instance_url,
        loading: false,
        error: null
      });
    } catch (error) {
      setStatus({
        isConnected: false,
        jiraInstanceUrl: '',
        loading: false,
        error: 'Failed to check Jira connection status'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchJiraSites = async () => {
    try {
      setLoading(true);
      const sitesData = await getJiraSites();
      setSites(sitesData);
      
      // Find the active site based on instance URL
      if (sitesData.length > 0 && status.jiraInstanceUrl) {
        const currentSite = sitesData.find(site => 
          status.jiraInstanceUrl.includes(site.id)
        );
        setActiveSite(currentSite || sitesData[0]);
      }
    } catch (error) {
      console.error('Error fetching Jira sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJiraProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await getJiraProjects();
      setProjects(projectsData);
    } catch (error) {
      console.error('Error fetching Jira projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectToJira = async () => {
    try {
      setIsAuthenticating(true);
      setLoading(true);
      
      // Store return URL and set pending flag in localStorage
      const returnUrl = `${window.location.origin}/me/integrations?status=success&from_auth=jira`;
      localStorage.setItem('jira_return_to', returnUrl);
      localStorage.setItem('jira_auth_pending', 'true');
      
      const { auth_url } = await initJiraAuth();
      
      // Display authenticating UI for a moment before redirect
      setTimeout(() => {
        // Redirect to Atlassian auth URL
        window.location.href = auth_url;
      }, 1000); // Short delay to show loading state
      
    } catch (error) {
      console.error('Failed to initialize Jira auth:', error);
      setStatus(prev => ({
        ...prev,
        error: 'Failed to connect to Jira'
      }));
      setIsAuthenticating(false);
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      await disconnectJira();
      setStatus({
        isConnected: false,
        jiraInstanceUrl: '',
        loading: false,
        error: null
      });
      setSites([]);
      setProjects([]);
      setActiveSite(null);
    } catch (error) {
      console.error('Failed to disconnect from Jira:', error);
      setStatus(prev => ({
        ...prev,
        error: 'Failed to disconnect from Jira'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchSite = async (siteId) => {
    try {
      setLoading(true);
      await switchJiraSite(siteId);
      await checkJiraConnection();
      await fetchJiraProjects();
      
      // Update active site
      const newActiveSite = sites.find(site => site.id === siteId);
      setActiveSite(newActiveSite);
    } catch (error) {
      console.error('Failed to switch Jira site:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderConnectedState = () => (
    <div className="bg-white rounded-lg border p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Jira Connected</h2>
          <p className="text-gray-600">Your Jira account is connected</p>
        </div>
        <div className="flex items-center">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
            Connected
          </span>
          <button
            onClick={handleDisconnect}
            disabled={loading}
            className={`inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-red-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
              loading ? 'opacity-75 cursor-not-allowed' : ''
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Disconnecting...
              </>
            ) : (
              'Disconnect'
            )}
          </button>
        </div>
      </div>
      
      {activeSite && (
        <div className="mt-4 mb-6">
          <h3 className="text-md font-medium text-gray-700 mb-2">Active Jira Site</h3>
          <div className="flex items-center">
            {activeSite.avatarUrl && (
              <img 
                src={activeSite.avatarUrl} 
                alt={activeSite.name} 
                className="w-8 h-8 rounded mr-2"
              />
            )}
            <div>
              <p className="font-medium">{activeSite.name}</p>
              <p className="text-sm text-gray-500">{activeSite.url}</p>
            </div>
          </div>
        </div>
      )}
      
      {sites.length > 1 && (
        <div className="mt-6">
          <h3 className="text-md font-medium text-gray-700 mb-2">Available Jira Sites</h3>
          <div className="space-y-3 mt-3">
            {sites.map(site => (
              <div key={site.id} className="flex items-center justify-between p-3 border rounded-md">
                <div className="flex items-center">
                  {site.avatarUrl && (
                    <img 
                      src={site.avatarUrl} 
                      alt={site.name} 
                      className="w-6 h-6 rounded mr-2"
                    />
                  )}
                  <div>
                    <p className="font-medium">{site.name}</p>
                    <p className="text-xs text-gray-500">{site.url}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleSwitchSite(site.id)}
                  disabled={activeSite?.id === site.id || loading}
                  className={`px-3 py-1 text-sm rounded-md ${
                    activeSite?.id === site.id
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : loading 
                        ? 'bg-blue-50 text-blue-400 cursor-not-allowed' 
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}
                >
                  {activeSite?.id === site.id ? 'Active' : loading ? 'Switching...' : 'Switch'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {projects.length > 0 && (
        <div className="mt-6">
          <h3 className="text-md font-medium text-gray-700 mb-2">Available Jira Projects</h3>
          <div className="mt-2 border rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projects.map(project => (
                  <tr key={project.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{project.key}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{project.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderConnectState = () => (
    <div className="bg-white rounded-lg border p-6 mt-6">
      <h2 className="text-xl font-semibold mb-2">JIRA Integration</h2>
      <p className="text-gray-600 mb-6">Connect to JIRA for issue tracking and project management</p>
      
      <div className="p-4 mb-6 bg-blue-50 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1 md:flex md:justify-between">
            <p className="text-sm text-blue-700">
              You will be redirected to Atlassian to authorize access to your Jira account.
            </p>
          </div>
        </div>
      </div>
      
      <button
        onClick={handleConnectToJira}
        disabled={isAuthenticating}
        className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
          isAuthenticating ? 'opacity-75 cursor-not-allowed' : ''
        }`}
      >
        {isAuthenticating ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Redirecting to Atlassian...
          </>
        ) : (
          'Connect JIRA'
        )}
      </button>
    </div>
  );

  // Render loading state while initially checking connection
  const renderLoadingState = () => (
    <div className="bg-white rounded-lg border p-6 mt-6 flex flex-col items-center justify-center py-10">
      <svg className="animate-spin h-10 w-10 text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <h2 className="text-xl font-semibold mb-2">Checking Connection Status</h2>
      <p className="text-gray-600">Please wait while we verify your Jira connection...</p>
    </div>
  );

  return (
    <div>
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="text-gray-600 hover:text-gray-900 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Integrations
        </button>
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-2">Connect JIRA</h1>
      
      {status.error && (
        <div className="mb-4 p-4 border-l-4 border-red-400 bg-red-50">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {status.error}
              </p>
            </div>
          </div>
        </div>
      )}

      {status.loading ? (
        renderLoadingState()
      ) : status.isConnected ? (
        renderConnectedState()
      ) : (
        renderConnectState()
      )}
    </div>
  );
};

export default JiraIntegrationForm; 